import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { omitBy } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { useNotifications } from 'providers/notifications/useNotifications';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router';
import { useListEventDestinationsQuery, useSyncWebhookDestinationMutation } from 'services/eventDestinations';
import { webhookFieldFromDestinations } from 'services/eventDestinations/planWebhookSync';
import { useGetMerchantQuery, useUpdateMerchantMutation } from 'services/merchants';
import { MerchantInfo, MerchantKycStatuses, MerchantStatus } from 'services/merchants/types';

import LoadingIndicator from 'components/loadingIndicator/loadingIndicator';

import { SetModalProps } from 'pages/merchant/route';
import { hasUserS4cRole } from 'pages/orders/view/utils';

import selectAccount, { selectAccountAccess } from 'state/selectors/accounts/accountSelector';
import { selectAccountType } from 'state/slices/accountSlice';
import { selectUserLevel } from 'state/slices/userSlice';

import { AccountAccessLevel, AccountType } from 'types/account';
import { APIError, ValidationErrorMeta } from 'types/error';
import { NotificationSeverity } from 'types/notifications';
import { UserAccessLevel } from 'types/user';

import { getValidationDetails } from 'utils/error';

import { hasKycStarted } from '../utils/hasKycStarted';
import { GeneralSection } from './general';
import { FORM_FIELDS, KEYS } from './keys';

interface GeneralSectionContainerProps extends SetModalProps {
  onOpenKyb: () => Promise<void>;
  onOpenAssociatedPerson: () => void;
}

export const GeneralSectionContainer = ({ setVisibleModal, onOpenKyb, onOpenAssociatedPerson }: GeneralSectionContainerProps) => {
  const { pushToast } = useNotifications();
  const matchId = useMatch(KEYS.ROUTE_MATCH_ID);
  const match = useMatch(KEYS.ROUTE_MATCH);
  const selectedMatch = matchId || match;
  const { account } = useSelector(selectAccount);
  const { uuid } = selectedMatch?.params ?? { uuid: account.uuid };
  const { data: merchantResponse, isLoading } = useGetMerchantQuery(uuid!, { skip: !uuid });
  const [merchant, setMerchant] = useState<Partial<MerchantInfo> | null>(merchantResponse?.data ?? null);
  const [values, setValues] = useState<Partial<MerchantInfo> | null>(merchant ?? null);
  const [saveMerchantInfo, { isLoading: isUpdating, error, reset }] = useUpdateMerchantMutation();
  const [syncWebhook, { isLoading: isSyncingWebhook, error: webhookError, reset: resetWebhook }] = useSyncWebhookDestinationMutation();
  const { data: destinations, isSuccess: destinationsLoaded } = useListEventDestinationsQuery(uuid ?? '', {
    skip: !uuid,
  });
  const webhookSeededFor = useRef<string | null>(null);
  const valuesRef = useRef(values);
  const merchantRef = useRef(merchant);
  valuesRef.current = values;
  merchantRef.current = merchant;
  const userLevel = useSelector(selectUserLevel);
  const accountType = useSelector(selectAccountType);
  const accountAccessLevel = useSelector(selectAccountAccess);
  const isMerchantViewOnly = accountAccessLevel === AccountAccessLevel.Viewer;
  const isAdmin = Boolean(userLevel && userLevel >= UserAccessLevel.Admin);
  const isPartnerViaPspOrGroupAccount =
    userLevel === UserAccessLevel.Partner &&
    (accountType === AccountType.PSP || accountType === AccountType.Pwc_Group) &&
    !isMerchantViewOnly;
  const canViewSensitiveMerchantGeneralFields = hasUserS4cRole(userLevel) || isPartnerViaPspOrGroupAccount || isMerchantViewOnly;
  const canManageSensitiveMerchantActions = isAdmin || isPartnerViaPspOrGroupAccount;

  useEffect(() => {
    const saveError = error || webhookError;
    if (saveError && 'data' in saveError) {
      const { errorMessage } = saveError.data as APIError;
      pushToast({
        severity: NotificationSeverity.error,
        message: errorMessage,
      });
    }
  }, [error, webhookError]);

  useEffect(() => {
    if (!merchantResponse || !isEqual(valuesRef.current, merchantRef.current)) {
      return;
    }

    const keepWebhook = webhookSeededFor.current === uuid;
    const nextMerchant = {
      ...merchantResponse.data,
      webhookUrl: keepWebhook ? valuesRef.current?.webhookUrl : merchantResponse.data.webhookUrl,
    };

    setValues(nextMerchant);
    setMerchant(nextMerchant);
  }, [uuid, merchantResponse]);

  useEffect(() => {
    if (!destinationsLoaded || !uuid || webhookSeededFor.current === uuid) {
      return;
    }

    webhookSeededFor.current = uuid;
    const destinationWebhook = webhookFieldFromDestinations(destinations?.data ?? []);
    const webhookUntouched = valuesRef.current?.webhookUrl === merchantRef.current?.webhookUrl;
    if (destinationWebhook === undefined || !webhookUntouched) {
      return;
    }

    setValues((prev) => ({ ...prev, webhookUrl: destinationWebhook }));
    setMerchant((prev) => ({ ...prev, webhookUrl: destinationWebhook }));
  }, [uuid, destinations, destinationsLoaded]);

  const handleFormValueChange = (key: string, value: any) => {
    setValues({
      ...values,
      [key]: value,
    });
    reset();
    resetWebhook();
  };

  const handleStatusChange = async (status: MerchantStatus) => {
    const result = await saveMerchantInfo({ status, uuid });
    if ((result as { error: FetchBaseQueryError })?.error) {
      return;
    }
    setValues({
      ...values,
      status,
    });
  };

  const readOnlyMode = Boolean(userLevel && userLevel === UserAccessLevel.Support) || isMerchantViewOnly;

  const couldUpdateKyb = hasKycStarted(merchant?.kyc?.kycStatus);

  const shouldHideUpdateKybButton =
    readOnlyMode || !canManageSensitiveMerchantActions || !couldUpdateKyb || merchant?.acceptedTOS?.length === 0;

  const isKybApproved = merchant?.kyc?.kycStatus === MerchantKycStatuses.Approved;

  const resetFormValues = () => {
    setValues(merchant);
    reset();
    resetWebhook();
  };

  const hiddenFields: Partial<Record<keyof MerchantInfo, boolean>> = {
    [FORM_FIELDS.MAIN_FIAT_CURRENCY]: !canViewSensitiveMerchantGeneralFields,
    [FORM_FIELDS.PROCESSING_FEE]: !canViewSensitiveMerchantGeneralFields,
    [FORM_FIELDS.STATUS]: !canViewSensitiveMerchantGeneralFields,
    [FORM_FIELDS.CHANGE_STATUS]: !canViewSensitiveMerchantGeneralFields,
    [FORM_FIELDS.MESH_SUBCLIENT_ID]: !canViewSensitiveMerchantGeneralFields,
    [FORM_FIELDS.MERCHANT_ID]: !canViewSensitiveMerchantGeneralFields,
    [FORM_FIELDS.BRIDGE_CUSTOMER_ID]: !canViewSensitiveMerchantGeneralFields,
    [FORM_FIELDS.KYC_STATUS]: !canViewSensitiveMerchantGeneralFields,
    [FORM_FIELDS.UPDATE_KYB_INFORMATION]: shouldHideUpdateKybButton,
    [FORM_FIELDS.WEBHOOK_URL]: !merchant?.pwcEnabled,
    [FORM_FIELDS.KYB_ERROR]: !canViewSensitiveMerchantGeneralFields || isKybApproved,
  };

  const saveFormValues = async () => {
    if (readOnlyMode || !uuid) {
      return;
    }
    const difference = omitBy(values, (value, key) => isEqual(value, merchant?.[key as keyof MerchantInfo]));
    const merchantChanges = omit(difference, FORM_FIELDS.WEBHOOK_URL);
    if (!isEmpty(merchantChanges)) {
      const result = await saveMerchantInfo({ ...merchantChanges, uuid });
      if ((result as { error: FetchBaseQueryError })?.error) {
        return;
      }
    }

    const webhookChanged = values?.webhookUrl !== merchant?.webhookUrl;
    if (merchant?.pwcEnabled && webhookChanged) {
      const synced = await syncWebhook({
        accountUuid: uuid,
        previousField: merchant?.webhookUrl,
        nextField: values?.webhookUrl,
      });
      if ('error' in synced) {
        return;
      }
    }

    setMerchant(values);
  };

  const isFormChanged = !isEqual(omit(values, FORM_FIELDS.STATUS), omit(merchant, FORM_FIELDS.STATUS));
  const saveErrorMeta = error ? ((error as FetchBaseQueryError).data as { meta: ValidationErrorMeta }).meta : undefined;

  const saveError = saveErrorMeta ? getValidationDetails(saveErrorMeta) : undefined;
  const isSaveAvailable = isFormChanged && isEmpty(saveError);
  const isUpdatingStatus = isUpdating && values?.status !== merchant?.status;

  if (isLoading) {
    return <LoadingIndicator padded />;
  }

  return (
    <GeneralSection
      merchant={{ ...merchant, ...values } as MerchantInfo}
      hiddenFields={hiddenFields}
      onFormValueChange={handleFormValueChange}
      onStatusChange={handleStatusChange}
      isFormChanged={isFormChanged}
      isSaveAvailable={isSaveAvailable}
      resetFormValues={resetFormValues}
      saveFormValues={saveFormValues}
      error={saveError}
      isUpdating={isUpdating || isSyncingWebhook}
      isUpdatingStatus={isUpdatingStatus}
      readOnlyMode={readOnlyMode}
      setVisibleModal={setVisibleModal}
      onOpenKyb={onOpenKyb}
      onOpenAssociatedPerson={onOpenAssociatedPerson}
    />
  );
};
